import { AnyAction } from 'redux';
import { App, Produkt, Produktlinie, TkvV2Vorbelegungdaten } from '@ps20-frontend/frontend-models';
import { produktReducer } from '@ps20-frontend/reducers';
import {
  GlobalActions,
  MasterProduktData,
} from '@ps20-frontend/shop-integration-redux';
import { PS20Logger } from '@ps20-frontend/logging';
import { Produktlinienname, TaasTarifrisikoschluesselTKV_V2 } from '@ps20/bff-models';
import { TkvActions } from '../tkvActions';
import { IAppState } from '../appState';
import { selectPossibleProduktlinien } from '../selectors/produkt.selector';

export const HEILBEHANDLUNGSSCHUTZ = 'HB';


type ActionHandler = (
  newSectionState: Produkt,
  action: AnyAction,
  state: IAppState,
  sectionState: Produkt,
) => Produkt;

const actionHandlers: Record<string, ActionHandler> = {
  [TkvActions.HEILBEHANDLUNGSSCHUTZ_CHANGED]: (newSectionState, action) => {
    const produktlinien: Produktlinie[] = newSectionState.produktlinien;
    if (
      produktlinien?.some(
        linie =>
          linie.selected &&
          linie.zusatzoptionen.some(option => option.name === HEILBEHANDLUNGSSCHUTZ),
      )
    ) {
      produktlinien
        .find(linie => linie.selected)
        .zusatzoptionen.find(option => option.name === HEILBEHANDLUNGSSCHUTZ).eigenschaft =
        action.payload;

      return { ...newSectionState, produktlinien };
    }
    return newSectionState;
  },

  [GlobalActions.PRODUKT_RECEIVED]: (newSectionState, action, state) => {
    const produktReceived = action.payload;
    if (produktReceived?.appId === App.TKV_V2 && produktReceived?.angebotsId === state.angebotsId) {
      const produktlinien: Produktlinie[] = newSectionState.produktlinien;
      produktlinien?.forEach(produktlinie => {
        produktlinie.zusatzoptionen?.forEach(zusatzoption => {
          if (zusatzoption.name === TaasTarifrisikoschluesselTKV_V2.TW) {
            zusatzoption.isSelected = state.vertragsoptionen.hasWechselbaustein === true;
          }
        });
        return produktlinie;
      });
      const wantsCoverageUpgrade = state.versicherungsnehmer.wantsCoverageUpgrade;
      return {
        ...newSectionState,
        produktlinien: selectPossibleProduktlinien(produktlinien, state, wantsCoverageUpgrade),
      };
    }
    return newSectionState;
  },

  [TkvActions.WANTS_COVERAGE_UPGRADE_CHANGED]: (newSectionState, action, state) => {
    const produktlinien: Produktlinie[] = newSectionState.produktlinien;
    const wantsCoverageUpgrade: boolean = action.payload;
    return {
      ...newSectionState,
      produktlinien: selectPossibleProduktlinien(produktlinien, state, wantsCoverageUpgrade),
    };
  },
  [GlobalActions.VORBELEGUNG_FOR_BEIBEHALTUNG_REQUESTED]: (
    newSectionState,
    action,
    state,
    sectionState,
  ) => {
    const produktlinien: Produktlinie[] = newSectionState.produktlinien;
    const actionPayload: TkvV2Vorbelegungdaten = action.payload;
    if (actionPayload.forApp === App.TKV_V2 && actionPayload.angebotsId === state.angebotsId) {
      if (actionPayload?.produktlinienname) {
        const produktlinieFound = sectionState.produktlinien.find(
          produktlinie => produktlinie.selected === true,
        );
        produktlinieFound.kurztext = actionPayload.verkaufsangebotKurzname;
      }
      return { ...newSectionState, produktlinien };
    }
    return newSectionState;
  },
  [TkvActions.TARIFWECHSEL_INCLUDED]: (newSectionState, action) => {
    const selected: boolean = action.payload;
    const newProduktLinien = newSectionState?.produktlinien?.map(produktlinie => {
      produktlinie.zusatzoptionen?.forEach(zusatzoption => {
        if (zusatzoption.name === TaasTarifrisikoschluesselTKV_V2.TW) {
          zusatzoption.isSelected = selected;
        }
      });
      return produktlinie;
    });
    return { ...newSectionState, produktlinien: newProduktLinien };
  },
  [TkvActions.PRODUKT_ZUSATZOPTIONEN_VORBELEGT]: (newSectionState, action) => {
    const tkvVorbelegungdaten: TkvV2Vorbelegungdaten = action.payload;
    const newProduktLinien = newSectionState?.produktlinien?.map(produktlinie => {
      produktlinie.zusatzoptionen?.forEach(zusatzoption => {
        zusatzoption.isSelected = !!tkvVorbelegungdaten?.vorvertragBausteinList.find(
          data => data?.taasTarifrisikoschluessel === zusatzoption?.name,
        );

        return zusatzoption;
      });
      return produktlinie;
    });
    return { ...newSectionState, produktlinien: newProduktLinien };
  },

  [TkvActions.ZUM_ANGEBOT_BUTTON_CLICKED]: (newSectionState, action, state) => {
    const produktlinien: Produktlinie[] = newSectionState.produktlinien;
    const wantsCoverageUpgrade: boolean = state.versicherungsnehmer.wantsCoverageUpgrade;
    const possibleProduktlinien = selectPossibleProduktlinien(
      produktlinien,
      state,
      wantsCoverageUpgrade,
    );
    return {
      ...newSectionState,
      produktlinien: possibleProduktlinien,
    };
  },

  [TkvActions.PRODUKTLINIEN_CHANGED]: (newSectionState, action) => {
    const produktlinien: Produktlinie[] = action.payload;
    return { ...newSectionState, produktlinien };
  },

  [TkvActions.PRODUKTLINIE_PREFILLED]: (newSectionState, action) => {
    const targetProduktlinienname: Produktlinienname = action.payload;
    const produktlinien: Produktlinie[] = newSectionState.produktlinien?.map(produktlinie => ({
      ...produktlinie,
      selected: produktlinie.produktName === targetProduktlinienname,
    }));
    return { ...newSectionState, produktlinien };
  },
};

export const tkvProduktReducer = function(
  sectionState: Produkt = null,
  action: AnyAction,
  state: IAppState,
): Produkt {
  if (
    (action.type === GlobalActions.PRODUKT_RECEIVED ||
      action.type === GlobalActions.PRODUKT_RECEIVED_AFTER_SAVE) &&
    (action.payload as MasterProduktData)?.appId === App.TKV_V2
  ) {
    const expected = state?.transientState?.expectedProdukttabelle;
    const got = (action.payload as MasterProduktData).produkttabelle;
    if (expected && got && got !== expected) {
      PS20Logger.info(
        `TKV_V2.produktReducer.rejected expected=${expected} got=${got}`,
        'stale PRODUKT_RECEIVED rejected',
        App.TKV_V2,
      );
      return sectionState;
    }
  }
  const newSectionState = produktReducer(sectionState, action, App.TKV_V2, state?.angebotsId);
  const handler = actionHandlers[action.type];
  return handler ? handler(newSectionState, action, state, sectionState) : newSectionState;
};
